import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Lightbulb, Rocket, Award } from 'lucide-react';
import { OUTCOMES, TRACKS, FAQ_ITEMS } from '@/constants';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function Home() {
  return (
    <div className="bg-white">
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1653669487404-09c3617c2b6c')`,
        }}
        data-testid="hero-section"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-unbounded font-black mb-6 uppercase"
            data-testid="hero-title"
          >
            A Residential AI Training + Builder Program
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl sm:text-2xl font-manrope mb-12 max-w-3xl mx-auto"
            data-testid="hero-subtitle"
          >
            We provide housing, structure, and mentorship so builders can ship real AI products.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
            data-testid="hero-cta-buttons"
          >
            <Link
              to="/apply"
              data-testid="hero-apply-button"
              className="bg-accent text-white px-10 py-5 font-bold uppercase tracking-wide hover:bg-accent-hover hover:shadow-electric transition-all inline-flex items-center justify-center gap-2"
            >
              Apply Now <ArrowRight size={20} />
            </Link>
            <Link
              to="/partners"
              data-testid="hero-partner-button"
              className="bg-transparent border-2 border-white text-white px-10 py-5 font-bold uppercase tracking-wide hover:bg-white hover:text-black transition-all inline-flex items-center justify-center"
            >
              Partner With Us
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-unbounded font-black mb-16 text-left">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-l-4 border-accent bg-gray-50 p-8"
              data-testid="step-1"
            >
              <Users className="text-accent mb-4" size={40} />
              <h3 className="text-2xl font-unbounded font-bold mb-4">Live with Builders</h3>
              <p className="font-manrope text-gray-700">8 residents per house. Cohort-based. Fully furnished. All-in on building.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="border-l-4 border-accent bg-gray-50 p-8"
              data-testid="step-2"
            >
              <Lightbulb className="text-accent mb-4" size={40} />
              <h3 className="text-2xl font-unbounded font-bold mb-4">Learn + Build</h3>
              <p className="font-manrope text-gray-700">Workshops, mentorship, build sprints. Ship real projects in 30 days.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="border-l-4 border-accent bg-gray-50 p-8"
              data-testid="step-3"
            >
              <Rocket className="text-accent mb-4" size={40} />
              <h3 className="text-2xl font-unbounded font-bold mb-4">Ship + Demo</h3>
              <p className="font-manrope text-gray-700">Demo your work to partners, investors, and the broader community.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-black text-white" data-testid="outcomes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-unbounded font-black mb-16 text-left">What You'll Achieve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {OUTCOMES.map((outcome, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4"
                data-testid={`outcome-${index}`}
              >
                <Award className="text-accent flex-shrink-0 mt-1" size={24} />
                <p className="text-lg font-manrope">{outcome}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="tracks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-unbounded font-black mb-16 text-left">Choose Your Track</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {TRACKS.map((track) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white border border-black/10 p-8 hover:border-accent transition-colors"
                data-testid={`track-${track.id}`}
              >
                <h3 className="text-2xl font-unbounded font-bold mb-4">{track.title}</h3>
                <p className="font-manrope text-gray-700 mb-6">{track.description}</p>
                <div className="space-y-2">
                  {track.outcomes.map((outcome) => (
                    <p key={outcome} className="text-sm font-manrope text-gray-600">• {outcome}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-unbounded font-black mb-16 text-left">FAQ</h2>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border border-black/10 bg-white px-6">
                <AccordionTrigger className="text-left font-manrope font-bold hover:text-accent">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="font-manrope text-gray-700">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-24 bg-accent text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-unbounded font-black mb-8">Ready to Build?</h2>
          <p className="text-xl font-manrope mb-12">Join the next cohort and ship real AI projects in 30 days.</p>
          <Link
            to="/apply"
            data-testid="cta-apply-button"
            className="bg-white text-accent px-10 py-5 font-bold uppercase tracking-wide hover:bg-black hover:text-white transition-all inline-flex items-center gap-2"
          >
            Apply Now <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
